import { createContext, useContext, useEffect, type ReactNode } from 'react'
import {
  useAuthStore,
  useUser,
  useIsAuthenticated,
  useAuthLoading,
  useAuthInitialized,
} from '@/features/auth/store'
import type { User } from '@/features/auth/types'

interface AuthContextValue {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  isInitialized: boolean
  hasRole: (role: string) => boolean
  hasPermission: (permission: string) => boolean
  hasAnyRole: (roles: string[]) => boolean
  hasAnyPermission: (permissions: string[]) => boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const user = useUser()
  const isAuthenticated = useIsAuthenticated()
  const isLoading = useAuthLoading()
  const isInitialized = useAuthInitialized()
  const { hasRole, hasPermission, hasAnyRole, hasAnyPermission, setInitialized } = useAuthStore()

  // Mark as initialized after first render if not already
  useEffect(() => {
    if (!isInitialized) {
      // Small delay to allow zustand persist to rehydrate
      const timer = setTimeout(() => {
        setInitialized(true)
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [isInitialized, setInitialized])

  const value: AuthContextValue = {
    user,
    isAuthenticated,
    isLoading: isLoading || !isInitialized,
    isInitialized,
    hasRole,
    hasPermission,
    hasAnyRole,
    hasAnyPermission,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuthContext() {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider')
  }
  return context
}
